// Offline-safe helpers shared by the Assistant turn capture (taskMixin) and the
// manual report-draft capture (reportDraftMixin). Both run while offline, where
// the browser rejects fetch() and device storage can be full.

/**
 * Convert a data URL ("data:image/jpeg;base64,…") to a Blob synchronously,
 * WITHOUT fetch(). Offline (real or DevTools-emulated) rejects fetch() for
 * every scheme — including `data:` — so the offline capture paths must decode
 * locally instead of round-tripping through fetch().
 *
 * @param {string} dataUrl
 * @returns {Blob}
 */
export function dataUrlToBlob(dataUrl) {
	const comma = typeof dataUrl === "string" ? dataUrl.indexOf(",") : -1;
	if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:") || comma === -1) {
		throw new Error("Invalid data URL");
	}

	const header = dataUrl.slice(5, comma);
	const isBase64 = /;base64$/i.test(header);
	const mime = header.replace(/;base64$/i, "") || "application/octet-stream";
	const payload = dataUrl.slice(comma + 1);

	if (!isBase64) {
		return new Blob([decodeURIComponent(payload)], { type: mime });
	}

	const binary = atob(payload);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new Blob([bytes], { type: mime });
}

/**
 * True when a storage write failed because the device is out of space.
 * QuotaExceededError is a DOMException (name + legacy code 22; Firefox 1014).
 *
 * @param {*} e
 * @returns {boolean}
 */
export function isQuotaExceeded(e) {
	return !!e && (e.name === "QuotaExceededError" || e.code === 22 || e.code === 1014);
}
